import Layout from "../Layout";
import { SiGithub } from "@icons-pack/react-simple-icons";
import {
  Eye,
  Shield,
  GitPullRequest,
  Server,
  Cloud,
  MessageSquare,
  CheckCircle,
  XCircle,
  Lock,
  Cpu,
  RefreshCw,
  Mail,
} from "lucide-react";
import { FAQ, FAQItem } from "@/components/marketing/FAQ";

const SECURITY_PRINCIPLES = [
  {
    icon: Eye,
    title: "Read-only by design",
    description:
      "Infralyst connects to your AWS accounts with read-only access. GitHub access is scoped to the repos you choose, and PRs are only created when you explicitly request one. You control what gets generated and what gets merged.",
  },
  {
    icon: Shield,
    title: "Least privilege, scoped access",
    description:
      "Access to your cloud accounts is granted via a Terraform-created IAM role with tightly scoped permissions. The GitHub App is limited to the repositories you select. You stay in control and can revoke access at any time.",
  },
  {
    icon: GitPullRequest,
    title: "Changes as code, not clicks",
    description:
      "Infralyst uses AI agents to read your Terraform and write the actual code change. Every change lands as a small, reviewable PR in your own repo. When a change requires downtime, the PR says so upfront. Your team reviews, tests, and merges every change.",
  },
  {
    icon: Server,
    title: "Hardened, private infrastructure",
    description:
      "We run on a Linux-based stack managed entirely as code, with no manual changes outside of version control. Services are deployed in hardened, private networks and are never directly reachable from the internet.",
  },
] as const;

const AWS_ACCESS_POINTS = [
  "You add the Infralyst Terraform module to your infra repo.",
  "The module creates a read-only IAM role so Infralyst can see your AWS resources and utilization.",
  "Access uses temporary credentials via AWS STS. No long-lived keys are stored or exchanged.",
  "We use that role to fetch the metadata and metrics needed to generate savings recommendations. We don't need or request access to your application data.",
] as const;

const GITHUB_ACCESS_POINTS = [
  "You install the Infralyst GitHub App in your GitHub organization and choose which infrastructure repositories it can see.",
  "Infralyst opens pull requests in those repos only, on dedicated branches.",
  "We never push directly to main or merge changes on your behalf.",
  "All webhook payloads are verified with HMAC-SHA256 signatures.",
] as const;

const SLACK_ACCESS_POINTS = [
  "If you connect Slack, we use it only to send notifications and trigger PR generation from your channels.",
  "We do not read or store the contents of your wider Slack workspace.",
  "All webhook payloads are verified with HMAC-SHA256 signatures.",
  "You can disconnect Slack at any time from your workspace settings.",
] as const;

const DATA_WE_STORE = [
  "Account details you give us (like name, email, and workspace info).",
  "Infrastructure metadata and utilization needed to generate savings recommendations.",
  "Configuration for your integrations (AWS role ARN, GitHub repo mappings, etc.).",
  "Product analytics that help us understand feature usage and improve Infralyst.",
] as const;

const DATA_WE_DONT_STORE = [
  "Your AWS access keys or secret keys. Access is via role assumption, not long-lived credentials.",
  "Your Terraform state files. We read Terraform files to generate PRs but don't store or cache state.",
  "Your application payloads or customer data.",
  "Database rows, S3 object contents, or logs from your production systems.",
  "Payment card details are handled by Stripe; our servers never see your full card number.",
  "Any secrets you don't explicitly configure in Infralyst.",
] as const;

const ENCRYPTION_POINTS = [
  {
    icon: Lock,
    title: "Encryption in transit and at rest",
    description:
      "All traffic between your browser, our API, and our integrations is protected with TLS. Sensitive data in our databases is encrypted at rest.",
  },
  {
    icon: Cpu,
    title: "AI that doesn't touch your data",
    description:
      "Recommendations are fully deterministic. No AI is involved in deciding what to resize. An open-source LLM writes the Terraform code change via OpenRouter. Your infrastructure data is never used for model training.",
  },
  {
    icon: Shield,
    title: "Limited employee access",
    description:
      "Access to production systems is limited to essential personnel and requires multi-factor authentication. We follow least-privilege principles internally.",
  },
  {
    icon: RefreshCw,
    title: "Keeping software up to date",
    description:
      "We regularly update operating systems, libraries, and dependencies to pick up security patches. New code is reviewed before it's deployed.",
  },
] as const;

function SecurityHero(): React.ReactElement {
  return (
    <section className="px-4 py-16 pt-40">
      <div className="max-w-4xl mx-auto text-center">
        <h1 className="mb-4 text-5xl font-bold leading-tight tracking-tight md:text-6xl">
          <span className="text-primary">Security</span> at Infralyst
        </h1>
        <p className="max-w-2xl mx-auto text-xl leading-relaxed text-surface-foreground/80">
          Infralyst is read-only first, conservative about changes, and
          transparent about how we handle your data.
        </p>
      </div>
    </section>
  );
}

function SecurityPrinciples(): React.ReactElement {
  return (
    <section className="py-16 px-4">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-4xl font-bold text-center mb-12">
          Our security principles
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {SECURITY_PRINCIPLES.map(({ icon: Icon, title, description }) => (
            <div
              key={title}
              className="p-6 rounded-3xl border bg-background/50 hover:border-primary/20 hover:bg-primary/5 transition-colors"
            >
              <div className="flex items-start gap-4">
                <div className="flex-none">
                  <div className="size-12 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                    <Icon className="size-6" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">{title}</h3>
                  <p className="text-surface-foreground/80 text-sm leading-relaxed">
                    {description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AccessDetails(): React.ReactElement {
  return (
    <section className="py-16 px-4">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-4xl font-bold text-center mb-12">
          AWS, GitHub, and Slack access
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          <div className="p-6 rounded-3xl border bg-background/50">
            <div className="size-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4">
              <Cloud className="size-6" />
            </div>
            <h3 className="text-xl font-bold mb-4">AWS access</h3>
            <ul className="space-y-3">
              {AWS_ACCESS_POINTS.map((point) => (
                <li
                  key={point}
                  className="text-surface-foreground/80 text-sm leading-relaxed flex items-start gap-2"
                >
                  <span className="size-1 rounded-full bg-primary flex-none mt-2" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="p-6 rounded-3xl border bg-background/50">
            <div className="size-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4">
              <SiGithub className="size-6" />
            </div>
            <h3 className="text-xl font-bold mb-4">GitHub & Terraform</h3>
            <ul className="space-y-3">
              {GITHUB_ACCESS_POINTS.map((point) => (
                <li
                  key={point}
                  className="text-surface-foreground/80 text-sm leading-relaxed flex items-start gap-2"
                >
                  <span className="size-1 rounded-full bg-primary flex-none mt-2" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="p-6 rounded-3xl border bg-background/50">
            <div className="size-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4">
              <MessageSquare className="size-6" />
            </div>
            <h3 className="text-xl font-bold mb-4">Slack</h3>
            <ul className="space-y-3">
              {SLACK_ACCESS_POINTS.map((point) => (
                <li
                  key={point}
                  className="text-surface-foreground/80 text-sm leading-relaxed flex items-start gap-2"
                >
                  <span className="size-1 rounded-full bg-primary flex-none mt-2" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function DataHandling(): React.ReactElement {
  return (
    <section className="py-16 px-4">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-4xl font-bold text-center mb-12">
          Data we store (and don't store)
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          <div className="p-6 rounded-3xl border bg-background/50">
            <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
              <CheckCircle className="size-5 text-green-500" />
              We store
            </h3>
            <ul className="space-y-4">
              {DATA_WE_STORE.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <CheckCircle className="size-4 text-green-500 mt-1 flex-none" />
                  <span className="text-surface-foreground/80 text-sm leading-relaxed">
                    {item}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className="p-6 rounded-3xl border bg-background/50">
            <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
              <XCircle className="size-5 text-surface-foreground/50" />
              We don't store
            </h3>
            <ul className="space-y-4">
              {DATA_WE_DONT_STORE.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <XCircle className="size-4 text-surface-foreground/50 mt-1 flex-none" />
                  <span className="text-surface-foreground/80 text-sm leading-relaxed">
                    {item}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <p className="text-center text-surface-foreground/80 mt-8 max-w-2xl mx-auto">
          We never sell your data or use it for advertising. You can delete your
          workspace and its associated data at any time. See our{" "}
          <a href="/privacy" className="text-primary hover:underline">
            Privacy Policy
          </a>{" "}
          for full details on data retention.
        </p>
      </div>
    </section>
  );
}

function EncryptionInfrastructure(): React.ReactElement {
  return (
    <section className="py-16 px-4">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-4xl font-bold text-center mb-12">
          Infrastructure security
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {ENCRYPTION_POINTS.map(({ icon: Icon, title, description }) => (
            <div
              key={title}
              className="p-6 rounded-3xl border bg-background/50 hover:border-primary/20 hover:bg-primary/5 transition-colors"
            >
              <div className="flex items-start gap-4">
                <div className="flex-none">
                  <div className="size-12 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                    <Icon className="size-6" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">{title}</h3>
                  <p className="text-surface-foreground/80 text-sm leading-relaxed">
                    {description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const FAQ_ITEMS: FAQItem[] = [
  {
    question: "Does Infralyst have write access to my AWS account?",
    answer:
      "No. Infralyst connects to your AWS accounts with read-only IAM permissions created by a Terraform module you add to your repo. Access uses temporary credentials via AWS STS, so no long-lived keys are stored or exchanged. Infralyst only reads infrastructure metadata and utilization metrics needed to generate savings recommendations.",
  },
  {
    question:
      "How does Infralyst access my Terraform code without storing state files?",
    answer:
      "Infralyst installs as a GitHub App scoped to the repositories you choose. It reads your Terraform files to generate code changes but never stores or caches your Terraform state. Every change is opened as a pull request on a dedicated branch. Infralyst never pushes to main or merges on your behalf.",
  },
  {
    question:
      "Is it safe to let a third-party tool open pull requests in my infrastructure repo?",
    answer:
      "Infralyst PRs go through the same review process as any other code change. Your team reviews, tests, and merges every PR. Infralyst never merges changes on its own or pushes directly to main. When a change requires downtime, the PR says so upfront.",
  },
  {
    question:
      "Does Infralyst send my infrastructure data to third-party AI providers?",
    answer:
      "Recommendations are fully deterministic and come from your utilization data alone. No AI is involved in the decision. When you generate a Terraform PR, an open-source LLM writes the code change via OpenRouter. Your infrastructure data is never used for model training.",
  },
  {
    question: "What data does Infralyst store from my AWS account?",
    answer:
      "Infralyst stores infrastructure metadata and utilization metrics needed to generate savings recommendations, plus integration configuration like your AWS role ARN and GitHub repo mappings. It does not store AWS access keys, Terraform state files, application payloads, S3 object contents, database rows, or production logs.",
  },
  {
    question:
      "How does Infralyst handle authentication to my cloud accounts?",
    answer:
      "Infralyst uses cross-account IAM role assumption via AWS STS. You deploy a Terraform module that creates a read-only role in your account. Infralyst assumes that role with temporary credentials for each session. No long-lived access keys or secrets are stored or exchanged between systems.",
  },
  {
    question:
      "Can I revoke Infralyst's access to my AWS or GitHub accounts?",
    answer:
      "Yes. You can remove the IAM role from your AWS account at any time by removing the Terraform module. For GitHub, uninstall the Infralyst GitHub App or change which repositories it can access from your organization settings. Slack can be disconnected from your workspace settings. You can revoke all access yourself, no support ticket needed.",
  },
  {
    question: "What happens if Infralyst's infrastructure is compromised?",
    answer:
      "Infralyst runs on hardened, private networks that are never directly reachable from the internet. All infrastructure is managed as code with no manual changes outside version control. Employee access to production requires multi-factor authentication and follows least-privilege principles. If our systems were compromised, an attacker would find only temporary read-only AWS credentials and no application data.",
  },
  {
    question:
      "How does Infralyst verify webhook payloads from GitHub and Slack?",
    answer:
      "All webhook payloads from both GitHub and Slack are verified using HMAC-SHA256 signatures. GitHub webhooks use per-repository secrets for additional isolation between customers.",
  },
  {
    question: "Does Infralyst read my Slack messages or workspace data?",
    answer:
      "No. If you connect Slack, Infralyst sends notifications, reacts to messages, and replies to threads to keep you updated on recommendation and PR status. It does not read or store your wider Slack workspace contents. You can disconnect Slack at any time from your workspace settings.",
  },
];

const DISCLOSURE_PROCESS = [
  "Acknowledge your message and confirm that we're looking into it.",
  "Investigate the issue and assess impact and severity.",
  "Resolve the problem where needed and, when appropriate, share what we changed.",
] as const;

function ResponsibleDisclosure(): React.ReactElement {
  return (
    <section className="py-16 px-4">
      <div className="max-w-3xl mx-auto">
        <h2 className="text-4xl font-bold mb-6 text-center">
          Security questions & responsible disclosure
        </h2>

        <p className="text-lg leading-relaxed text-surface-foreground/80 mb-8 text-center">
          If you've found a potential security or privacy issue, or have a
          question about how Infralyst handles data, please contact our security
          team.
        </p>

        <div className="p-6 rounded-3xl border bg-background/50 mb-8">
          <div className="flex items-center gap-3 justify-center mb-4">
            <Mail className="size-5 text-primary" />
            <a
              href="mailto:security@infralyst.io"
              className="text-primary hover:underline text-lg font-medium"
            >
              security@infralyst.io
            </a>
          </div>

          <p className="text-surface-foreground/80 text-sm leading-relaxed mb-4">
            When you email us, please include as much detail as you can, along
            with steps to reproduce if applicable.
          </p>

          <p className="text-surface-foreground/80 text-sm leading-relaxed mb-3">
            Once we receive your report, we will:
          </p>

          <ul className="space-y-2 mb-4">
            {DISCLOSURE_PROCESS.map((step, index) => (
              <li
                key={index}
                className="flex items-start gap-3 text-surface-foreground/80 text-sm leading-relaxed"
              >
                <span className="text-primary font-medium">{index + 1}.</span>
                <span>{step}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className="text-surface-foreground/80 text-sm leading-relaxed text-center">
          We ask that you follow responsible disclosure and give us a chance to
          fix issues before sharing them publicly.
        </p>
      </div>
    </section>
  );
}

export default function Show(): React.ReactElement {
  return (
    <Layout
      title="Security"
      description="Infralyst is built to be read-only first, conservative about changes, and transparent about how we handle your data."
    >
      <SecurityHero />
      <SecurityPrinciples />
      <AccessDetails />
      <DataHandling />
      <EncryptionInfrastructure />
      <ResponsibleDisclosure />
      <FAQ
        items={FAQ_ITEMS}
        title="Security and Data Privacy FAQ"
        highlightWord="Security"
        className="py-16 px-4"
      />
    </Layout>
  );
}
